import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { Settings, Bot, Zap, Layers } from 'lucide-react';
import { useSettings } from '@/contexts/settings';
import { GuidanceProvider } from '@/contexts/settings/types';

interface PrayerCompanionSettingsProps {
  onProviderChange?: (provider: GuidanceProvider) => void;
}

export const PrayerCompanionSettings: React.FC<PrayerCompanionSettingsProps> = ({
  onProviderChange
}) => {
  const { settings, setGuidanceProvider, loading } = useSettings();
  const { toast } = useToast();

  const handleProviderChange = async (newProvider: GuidanceProvider) => {
    try {
      await setGuidanceProvider(newProvider);
      
      // Notify parent component
      onProviderChange?.(newProvider);
      
      toast({
        title: "Settings Updated",
        description: `Provider changed to ${newProvider === 'openai' ? 'OpenAI' : newProvider === 'perplexity' ? 'Perplexity' : 'Hybrid Mode'}`,
      });
    } catch (error) {
      console.error('Failed to update provider:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update provider setting. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getProviderDescription = (providerType: GuidanceProvider) => {
    switch (providerType) {
      case 'openai':
        return 'Fast, reliable guidance with structured prayer outlines and reflection prompts.';
      case 'perplexity':
        return 'Enhanced guidance with live commentary insights and cross-references from web sources.';
      case 'hybrid':
        return 'Best of both worlds - OpenAI structure enhanced with Perplexity commentary.';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Card className="w-full max-w-2xl">
        <CardContent className="p-6">
          <div className="text-center">Loading settings...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Prayer Companion Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label className="text-base font-medium mb-4 block">
            AI Provider Selection
          </Label>
          <RadioGroup 
            value={settings.guidanceProvider} 
            onValueChange={handleProviderChange}
            className="space-y-4"
          >
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="openai" id="openai" />
                <Label htmlFor="openai" className="flex items-center gap-2 cursor-pointer">
                  <Bot className="h-4 w-4 text-blue-500" />
                  OpenAI (Default)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {getProviderDescription('openai')}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="perplexity" id="perplexity" />
                <Label htmlFor="perplexity" className="flex items-center gap-2 cursor-pointer">
                  <Zap className="h-4 w-4 text-purple-500" />
                  Perplexity (Enhanced)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {getProviderDescription('perplexity')}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hybrid" id="hybrid" />
                <Label htmlFor="hybrid" className="flex items-center gap-2 cursor-pointer">
                  <Layers className="h-4 w-4 text-green-500" />
                  Hybrid Mode (Experimental)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                {getProviderDescription('hybrid')}
              </p>
            </div>
          </RadioGroup>
        </div>

        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Settings are automatically saved and will apply to all new prayer guidance requests.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Hook to get current provider setting - now uses Settings Context
export const usePrayerCompanionProvider = (): GuidanceProvider => {
  const { settings } = useSettings();
  return settings.guidanceProvider;
};